import type { ReactNode } from 'react';
import type { MatchView, Side } from '../../engine';
import { t } from '../../i18n';
import type { Controller } from '../../app/matchHost';
import { CardBackArt } from './CardView';
import { PawnFigure } from './PawnFigure';
import { RollingNumber } from './RollingNumber';

/** Round wins as pips: filled for each round won, out of the wins needed (§4.3). */
export function WinPips({ won, of }: { won: number; of: number }) {
  return (
    <span className="win-pips" aria-hidden="true">
      {Array.from({ length: of }, (_, i) => (
        <span key={i} className={`pip ${i < won ? 'pip-on' : ''}`} />
      ))}
    </span>
  );
}

/**
 * One player in a single line: piece, name (with round wins and what they are doing under it,
 * so the name keeps the room), what they hold and the round score. It opens the player's
 * details (Epitaph, reserves, spent cards).
 */
export function PlayerStrip({
  v,
  side,
  controller,
  isViewer,
  showCounts,
  decider,
  thinking,
  heldRound,
  reduceMotion = false,
  onOpen,
  kit,
  ante,
  className = '',
}: {
  v: MatchView;
  side: Side;
  controller: Controller;
  /** The side whose hand is shown on this device. */
  isViewer: boolean;
  /** Show hand and deck sizes (for a hand that is not shown). */
  showCounts: boolean;
  /** Whose decision it is, when the view shown is a drafted one (default: the view's). */
  decider?: Side | null;
  thinking: boolean;
  /**
   * The round score to show instead of the view's: the match's own while a turn is drafted, and
   * the score before a chain's while that chain is still being counted (it rolls up after).
   */
  heldRound?: number;
  reduceMotion?: boolean;
  onOpen?: (side: Side) => void;
  /** The Long Night: the side's Mantel and Curio pockets, which can be tapped themselves. */
  kit?: ReactNode;
  /** The Long Night: the Ante, written beside the score. */
  ante?: ReactNode;
  className?: string;
}) {
  const p = v.players[side];
  const c = v.config.constants;
  const deciding =
    decider !== undefined ? decider === side : v.decider === side && v.phase !== 'MATCH_OVER';
  const headStart = side === 'N' ? v.config.northHeadStart : 0;
  const epitaph = p.epitaphs[0];
  const roundScore = heldRound ?? p.roundScore;
  const status = thinking
    ? t('strip.thinking')
    : p.passed
      ? t('match.passed')
      : deciding && v.phase !== 'CHOOSE_STARTER'
        ? t('strip.turn')
        : null;
  // One sentence for screen readers, in reading order.
  const summary = [
    `${controller.name} (${t(`side.${side}`)})`,
    t('match.winsOf', { n: p.roundWins, of: c.ROUND_WINS_NEEDED }) + ` ${t('match.roundWins')}`,
    `${t('match.roundScore')} ${roundScore}${headStart > 0 ? ` (${t('match.headStart', { n: headStart })})` : ''}`,
    `${t('match.total')} ${p.totalScore}`,
    showCounts ? t('match.handDeck', { hand: p.handCount, deck: p.deckCount }) : '',
    epitaph ? t('strip.hasEpitaph') : '',
    p.reserves.length > 0 ? `${t('match.reserves')} ${p.reserves.length}` : '',
    status ?? '',
  ]
    .filter(Boolean)
    .join(', ');
  const who = (
    <>
      <span className="strip-piece" aria-hidden="true">
        <PawnFigure side={side} crowned={false} immune={false} inscription={null} />
      </span>
      <span className="strip-who" aria-hidden="true">
        <span className="strip-name">
          {controller.name}
          {isViewer && controller.kind === 'human' && controller.name !== t('match.you') && (
            <span className="strip-you"> · {t('match.you')}</span>
          )}
        </span>
        <span className="strip-sub">
          <WinPips won={p.roundWins} of={c.ROUND_WINS_NEEDED} />
          {status && (
            <span
              className={`strip-status ${thinking ? 'is-thinking' : p.passed ? 'is-passed' : 'is-turn'}`}
            >
              {thinking && <span className="dots" />}
              {status}
            </span>
          )}
        </span>
      </span>
    </>
  );
  const rest = (
    <>
      <span className="strip-holdings" aria-hidden="true">
        {epitaph && (
          <span className="strip-tag strip-epitaph" title={t('card.epitaph')}>
            <span className="tomb" />
          </span>
        )}
        {p.reserves.length > 0 && (
          <span className="strip-tag" title={t('match.reserves')}>
            <span className="strip-reserve" />
            {p.reserves.length - p.reservesPawnedThisRound.length}
          </span>
        )}
        {showCounts && (
          <>
            <span className="strip-tag" data-strip-hand={side} title={t('match.hand')}>
              <span className="mini-card">
                <CardBackArt />
              </span>
              {p.handCount}
            </span>
            <span className="strip-tag strip-deck" data-strip-deck={side} title={t('match.deck')}>
              <span className="mini-stack" />
              {p.deckCount}
            </span>
          </>
        )}
      </span>
      {ante}
      <span className="strip-score" aria-hidden="true">
        <RollingNumber className="strip-round" value={roundScore} reduceMotion={reduceMotion} />
        <span className="strip-total">
          Σ <RollingNumber value={p.totalScore} reduceMotion={reduceMotion} />
        </span>
      </span>
    </>
  );
  const content = (
    <>
      {who}
      <span className="strip-fill" />
      {rest}
    </>
  );
  const classes = `strip side-${side} ${deciding ? 'is-deciding' : ''} ${isViewer ? 'is-viewer' : ''} ${className}`;
  // With a kit the strip holds buttons of its own: the name opens the details instead.
  if (kit)
    return (
      <section className={`${classes} has-kit`} aria-label={summary} data-strip={side}>
        {onOpen ? (
          <button
            type="button"
            className="strip-open"
            aria-label={`${summary}. ${t('strip.details')}`}
            onClick={() => onOpen(side)}
          >
            {who}
          </button>
        ) : (
          who
        )}
        <span className="strip-fill" />
        {kit}
        {rest}
      </section>
    );
  return onOpen ? (
    <button
      type="button"
      className={classes}
      aria-label={`${summary}. ${t('strip.details')}`}
      data-strip={side}
      onClick={() => onOpen(side)}
    >
      {content}
    </button>
  ) : (
    <section className={classes} aria-label={summary} data-strip={side}>
      {content}
    </section>
  );
}
